import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import useLocalStorage from "../Labs/useLocalStorage";

// "Writing prompts so I don't have to think about what to write." A slim card above the
// columns with one prompt in the target language, at a level, optionally on a topic and
// modelled on an exam's writing task. Options stay folded away so the page stays compact.
//
// Options apply when submitted, not as they change - setting a level and ticking exam
// prep is one decision, not two requests. There's only ever one "New idea" on screen:
// in the header while the options are folded (reroll with the same options), and at the
// end of the options row while they're open, where it reads as their submit button.
// It's highlighted while the options differ from the current prompt's.
export default function WritingIdea({
  baseLanguageName, levels, onClose, open, promptUrl, request, storagePrefix, targetLanguageIso,
}) {
  const [level, setLevel] = useLocalStorage(storagePrefix, "ideaLevel", "b1");
  const [topic, setTopic] = useLocalStorage(storagePrefix, "ideaTopic", "");
  const [exam, setExam] = useLocalStorage(storagePrefix, "ideaExam", false);
  const [idea, setIdea] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTranslation, setShowTranslation] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  // Sent back so the next prompt is a different one.
  const previous = useRef([]);

  // The options the current prompt was generated with, to tell when they've changed.
  const [usedOptions, setUsedOptions] = useState(null);
  const options = { exam, level, topic: topic.trim() };
  const optionsChanged = !!usedOptions && JSON.stringify(usedOptions) !== JSON.stringify(options);

  const generate = useCallback(async () => {
    const sent = { exam, level, topic: topic.trim() };
    setLoading(true);
    setError(null);
    setShowTranslation(false);

    try {
      const result = await request(promptUrl, { ...sent, previous: previous.current });
      previous.current = [...previous.current, result.prompt].slice(-5);
      setIdea(result);
      setUsedOptions(sent);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [exam, level, promptUrl, request, topic]);

  // The first open is asking for an idea - once, on mount. After that the parent keeps
  // this mounted and only hides it, so reopening shows the same idea.
  const generateRef = useRef(generate);
  useEffect(() => {
    generateRef.current();
  }, []);

  const newIdeaButton = (type) => (
    <button
      className={`btn btn-xs ${optionsChanged ? "btn-success" : "btn-default"}`}
      disabled={loading}
      onClick={type === "button" ? generate : undefined}
      title={optionsChanged ? "Get an idea with these options" : undefined}
      type={type}
    >
      New idea
    </button>
  );

  if (!open) return null;

  return (
    <div className="ftf-idea">
      <div className="ftf-idea-head">
        <span className="ftf-label">
          Writing idea{idea && idea.exam ? ` · ${idea.exam}` : ""}
        </span>
        <div className="ftf-idea-actions">
          {!showOptions && newIdeaButton("button")}
          <button
            aria-expanded={showOptions}
            className="btn btn-default btn-xs"
            onClick={() => setShowOptions(!showOptions)}
            type="button"
          >
            Options
          </button>
          <button aria-label="Close" className="ftf-idea-close" onClick={onClose} type="button">×</button>
        </div>
      </div>

      {showOptions && (
        <form
          className="ftf-idea-options"
          onSubmit={(event) => {
            event.preventDefault();
            generate();
          }}
        >
          <label htmlFor="ftf-idea-level">
            Level
            <select
              className="form-control input-sm"
              id="ftf-idea-level"
              onChange={(event) => setLevel(event.target.value)}
              value={level}
            >
              {Object.entries(levels).map(([value, name]) => <option key={value} value={value}>{name}</option>)}
            </select>
          </label>
          <label htmlFor="ftf-idea-topic">
            Topic
            <input
              className="form-control input-sm"
              id="ftf-idea-topic"
              maxLength={100}
              onChange={(event) => setTopic(event.target.value)}
              placeholder="any"
              type="text"
              value={topic}
            />
          </label>
          <label htmlFor="ftf-idea-exam">
            <input
              checked={exam}
              id="ftf-idea-exam"
              onChange={(event) => setExam(event.target.checked)}
              type="checkbox"
            />
            Exam prep
          </label>
          {newIdeaButton("submit")}
        </form>
      )}

      {loading && <div className="ftf-muted">Thinking of something…</div>}
      {!loading && error && <div className="ftf-error">{error}</div>}
      {!loading && !error && idea && (
        <>
          <div className="ftf-idea-prompt" lang={targetLanguageIso}>{idea.prompt}</div>
          {showTranslation
            ? (
              <div className="ftf-idea-translation">
                {idea.translation}{" "}
                <button className="ftf-idea-reveal" onClick={() => setShowTranslation(false)} type="button">
                  Hide
                </button>
              </div>
            )
            : (
              <button className="ftf-idea-reveal" onClick={() => setShowTranslation(true)} type="button">
                Show in {baseLanguageName}
              </button>
            )}
        </>
      )}
    </div>
  );
}

WritingIdea.propTypes = {
  baseLanguageName: PropTypes.string.isRequired,
  levels: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  promptUrl: PropTypes.string.isRequired,
  request: PropTypes.func.isRequired,
  storagePrefix: PropTypes.string.isRequired,
  targetLanguageIso: PropTypes.string,
};
